import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { subscribeDigest, unsubscribeDigest, getSubscriberStats } from "../lib/api";
import { SectionTitle, Loader } from "../components/Shared";
import { Mail, MessageCircle, Check, AlertCircle, Sun, X, Users } from "lucide-react";

const KEY = "edusphere_digest_sub_v1";

export default function Subscribe() {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [hour, setHour] = useState(7);
  const [language, setLanguage] = useState("english");
  const [level, setLevel] = useState("school");
  const [status, setStatus] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    getSubscriberStats().then(setStats).catch(() => {});
    try {
      const saved = JSON.parse(localStorage.getItem(KEY) || "null");
      if (saved) {
        setEmail(saved.email || "");
        setPhone(saved.phone || "");
        setName(saved.name || "");
        setHour(saved.delivery_hour_ist || 7);
        setStatus(saved);
      }
    } catch (e) {}
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email && !phone) {
      setErr("Add an email, a phone number, or both.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        email: email.trim() || null,
        phone: phone.trim() || null,
        name: name.trim() || null,
        language, level,
        delivery_hour_ist: parseInt(hour),
      };
      const res = await subscribeDigest(payload);
      setStatus(res);
      localStorage.setItem(KEY, JSON.stringify(res));
      const fresh = await getSubscriberStats();
      setStats(fresh);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Could not subscribe. Check your email/phone.");
    } finally {
      setLoading(false);
    }
  };

  const unsub = async () => {
    if (!status) return;
    setLoading(true);
    setErr("");
    try {
      await unsubscribeDigest({
        email: status.email || null,
        phone: status.phone || null,
      });
      setStatus(null);
      localStorage.removeItem(KEY);
      const fresh = await getSubscriberStats();
      setStats(fresh);
    } catch (ex) {
      setErr("Could not unsubscribe.");
    } finally {
      setLoading(false);
    }
  };

  const isSubscribed = !!status;

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="subscribe-page">
      <SectionTitle
        kicker="Daily digest"
        title="Today's story, in your inbox."
        subtitle="One short, illustrated story every morning — mythology, science, or a moral classic. Built for kids, parents, and lifelong learners."
      />

      {/* Stats strip */}
      {stats && (
        <div className="card p-5 md:p-6 mb-7 grid grid-cols-3 gap-3" data-testid="subscribe-stats">
          <div data-testid="sub-stats-total">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#3A5A40] font-semibold flex items-center gap-1.5">
              <Users size={12} /> Subscribers
            </div>
            <div className="font-heading font-extrabold text-2xl mt-1">{stats.total}</div>
          </div>
          <div data-testid="sub-stats-email">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#3A5A40] font-semibold">Email</div>
            <div className="font-heading font-extrabold text-2xl mt-1">{stats.email_subscribers}</div>
          </div>
          <div data-testid="sub-stats-phone">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#3A5A40] font-semibold">WhatsApp waitlist</div>
            <div className="font-heading font-extrabold text-2xl mt-1">{stats.phone_waitlist}</div>
          </div>
        </div>
      )}

      {stats?.email_send_mode === "mocked" && (
        <div className="bg-[#FBE7DF] border border-[#E07A5F] rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="subscribe-mock-banner">
          <AlertCircle size={18} className="text-[#E07A5F] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-[#1A1A1A]">
            <strong>Demo mode:</strong> emails are not actually sent (no <code>RESEND_API_KEY</code> set yet).
            Subscriptions are captured and the "Subscribers" KPI updates live — drop your Resend key into <code>backend/.env</code> and restart the backend to go live.
          </div>
        </div>
      )}

      {/* Subscribed state */}
      {isSubscribed ? (
        <div className="card p-7 md:p-8 fadeIn" data-testid="subscribe-confirmed">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#EAECE6] grid place-items-center text-[#3A5A40]">
              <Check size={20} />
            </div>
            <div>
              <div className="font-heading text-xl font-bold">You're in! 🎉</div>
              <div className="text-sm text-[#4A4A4A]">We'll send today's story to your inbox each morning.</div>
            </div>
          </div>
          <ul className="text-sm text-[#4A4A4A] space-y-1.5 mt-4">
            {status.email && <li>📧 <strong>Email:</strong> {status.email} {status.email_mocked && <span className="text-[#E07A5F] text-xs">(mocked)</span>}</li>}
            {status.phone && <li>📱 <strong>WhatsApp waitlist:</strong> {status.phone}</li>}
            <li>🕖 <strong>Delivery:</strong> {String(status.delivery_hour_ist || 7).padStart(2,"0")}:00 IST</li>
          </ul>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/today" className="btn-primary inline-flex items-center gap-2" data-testid="subscribed-read-today">
              <Sun size={14} /> Read today's story
            </Link>
            <button onClick={unsub} disabled={loading} className="btn-outline inline-flex items-center gap-2" data-testid="subscribe-unsubscribe">
              <X size={14} /> Unsubscribe
            </button>
          </div>
          {err && <div className="text-[#D9534F] text-sm mt-3" data-testid="subscribe-error">{err}</div>}
        </div>
      ) : (
        <form className="card p-7 md:p-8 grid gap-4" onSubmit={submit} data-testid="subscribe-form">
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#3A5A40] font-semibold mb-1 flex items-center gap-1.5">
                <Mail size={12} /> Email
              </div>
              <input
                type="email"
                className="input-field"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="subscribe-email"
              />
            </label>
            <label className="block">
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#3A5A40] font-semibold mb-1 flex items-center gap-1.5">
                <MessageCircle size={12} /> WhatsApp number (waitlist)
              </div>
              <input
                type="tel"
                className="input-field"
                placeholder="+91 9XXXXX XXXXX"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="subscribe-phone"
              />
            </label>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <label className="block">
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#3A5A40] font-semibold mb-1">Your name (optional)</div>
              <input className="input-field" value={name} onChange={(e) => setName(e.target.value)} data-testid="subscribe-name" />
            </label>
            <label className="block">
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#3A5A40] font-semibold mb-1">Language</div>
              <select className="input-field" value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="subscribe-language">
                <option value="english">English</option>
                <option value="hindi">Hindi</option>
                <option value="spanish">Spanish</option>
                <option value="french">French</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[11px] tracking-[0.18em] uppercase text-[#3A5A40] font-semibold mb-1">Delivery (IST)</div>
              <select className="input-field" value={hour} onChange={(e) => setHour(parseInt(e.target.value))} data-testid="subscribe-hour">
                {[6,7,8,9,18,19,20].map(h => <option key={h} value={h}>{String(h).padStart(2,"0")}:00</option>)}
              </select>
            </label>
          </div>
          {err && <div className="text-[#D9534F] text-sm" data-testid="subscribe-error">{err}</div>}
          <div className="flex flex-wrap gap-3 items-center mt-2">
            <button type="submit" disabled={loading} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="subscribe-submit">
              {loading ? "Subscribing…" : "Get the daily story"}
            </button>
            <span className="text-xs text-[#4A4A4A]">No spam. Unsubscribe anytime in one click.</span>
          </div>
        </form>
      )}

      {/* Why narrative */}
      <div className="grid md:grid-cols-3 gap-4 mt-10" data-testid="subscribe-why">
        <div className="card p-5"><div className="text-[11px] uppercase tracking-[0.18em] text-[#3A5A40] font-semibold mb-2">3 minutes</div><div className="font-heading font-bold">Bite-sized</div><div className="text-sm text-[#4A4A4A] mt-1">One illustrated story a day. Read with your child over breakfast.</div></div>
        <div className="card p-5"><div className="text-[11px] uppercase tracking-[0.18em] text-[#E07A5F] font-semibold mb-2">7 AM IST</div><div className="font-heading font-bold">Classroom-ready</div><div className="text-sm text-[#4A4A4A] mt-1">Reach your inbox just before school. Pair with the moral &amp; reflection.</div></div>
        <div className="card p-5"><div className="text-[11px] uppercase tracking-[0.18em] text-[#3A5A40] font-semibold mb-2">Forever free</div><div className="font-heading font-bold">For every learner</div><div className="text-sm text-[#4A4A4A] mt-1">English, Hindi, Spanish &amp; more. WhatsApp digest coming soon.</div></div>
      </div>
    </div>
  );
}
